import { LinearFilter, TextureLoader } from 'three';

import { getRootOffset } from '../../../helpers';

import Gallery from '../gallery/index';

export default class CaseStudy {

	selector = 'case-studies';

	hero;

	constructor({
		scene,
		clock,
		sizes,
		geometry,
		renderer
	}) {

		const container = document.querySelector(`[data-uri*="${this.selector}"]`);

		this.el = {
			container,
			images: container.querySelectorAll('[data-gl-image]'),
			galleries: container.querySelectorAll('[data-gl-gallery]')
		};

		this.loader = new TextureLoader();

		this.renderer = renderer;

		this.textures = [];
		this.scene = scene;
		this.clock = clock;
		this.sizes = sizes;
		this.geometry = geometry;

		this.isPreloaded = false;

		this.scroll = null;

		this.images = [];
		this.galleries = [];

		// GL components
		this.glGalleries = [];

		this.types = {
			gallery: 'gallery'
		};
	}

	init(isPreloaded) {
		this.isPreloaded = isPreloaded;
		this.gather();
	}

	loadTexture(asset, slug, type, index, onComplete) {
		this.loader.load(asset.url, texture => {
			texture.imageType = type;
			texture.imageWidth = asset.width;
			texture.imageHeight = asset.height;
			texture.uid = asset.uid;
			texture.parentUid = asset.parentUid ? asset.parentUid : null;
			texture.index = index;
			texture.slug = slug;
			onComplete(texture);
		});
	}

	gather() {
		const loads = [];

		for (const [index, image] of this.el.images.entries()) {
			const el = image.querySelector('img');
			const ref = image.querySelector('[data-gl-ref]');
			const offsets = getRootOffset(image);
			const parent = image.closest('[data-gl-parent-uid]');

			const asset = {
				url: el.src,
				width: ref.offsetWidth,
				height: ref.offsetHeight,
				uid: image.dataset.glUid,
				parentUid: parent ? parent.dataset.glParentUid : null,
			};

			loads.push(new Promise((resolve) => {
				this.loadTexture(asset, this.selector, 'gallery', index, (texture) => {
					texture.generateMipmaps = false;
					texture.minFilter = LinearFilter;
					texture.needsUpdate = true;

					this.renderer.initTexture(texture, 0);
					this.textures.push(texture);
					resolve();
				});
			}));

			this.images.push({
				el,
				ref,
				index,
				cache: image,
				parentUid: parent ? parent.dataset.glParentUid : null,
				uid: image.dataset.glUid,
				type: image.dataset.glType,
				bounds: {
					imageWidth: ref.offsetWidth,
					imageHeight: ref.offsetHeight,
					width: image.offsetWidth,
					height: image.offsetHeight,
					top: offsets.top,
					left: offsets.left
				}
			});
		}

		Promise.all(loads).then(() => {
			for (const gallery of this.el.galleries) {
				const { glParentUid, glType } = gallery.dataset;
				const slides = gallery.querySelectorAll('[data-gl-image]');
				const controls = gallery.querySelectorAll('[data-gl-control]');
				const assets = [];

				for (const slide of slides) {
					const { glUid } = slide.dataset;
					const image = this.mapSlideImage(glUid, glParentUid);
					const texture = this.mapSlideTexture(image, glParentUid, this.types.gallery);
					assets.push({ image, texture });
				}

				this.galleries.push({
					controls,
					el: gallery,
					type: glType,
					assets,
					sizes: this.sizes
				});
			}

			this.createMeshes();
			this.show();
		});
	}

	/**
	 * Create meshes
	 */
	createMeshes() {
		for (const gallery of this.galleries) {
			if (gallery.type === this.types.gallery) {
				this.glGalleries.push(new Gallery({
					...gallery,
					geometry: this.geometry
				}));
			}
		}
	}

	mapTexture(image, type) {
		const texture = this.textures.find(match => match.imageType === type && match.uid === image.uid);
		return texture;
	}

	mapSlideTexture(image, parentUid, type) {
		for (const texture of this.textures) {
			if (texture.uid === image.uid && texture.parentUid === parentUid && texture.imageType === type) {
				return texture;
			}
		}
	}

	mapSlideImage(uid, parentUid) {
		const image = this.images.find(match => match.uid === uid && match.parentUid === parentUid);
		return image;
	}

	show() {
		for (const gallery of this.glGalleries) {
			this.scene.add(gallery);
			gallery.show();
		}
	}

	hide() {
		const promises = [];

		if (this.glGalleries && this.glGalleries.length) {
			for (let gallery of this.glGalleries) {
				const promise = gallery.hide(() => {
					this.scene.remove(gallery);
					gallery.destroy();
					gallery = null;
				});
				promises.push(promise);
			}
		}

		return Promise.all(promises);
	}

	/**
	 * Resize
	 */

	resize({ sizes }) {
		this.sizes = sizes;

		if (this.glSplits && this.glSplits.length) {
			this.resizeMeshes(this.glSplits);
		}

		if (this.glHeroes && this.glHeroes.length) {
			this.resizeMeshes(this.glHeroes);
		}

		if (this.glSplitSliders && this.glSplitSliders.length) {
			this.resizeMeshes(this.glSplitSliders);
		}

		if (this.glGalleries && this.glGalleries.length) {
			this.resizeMeshes(this.glGalleries);
		}
	}

	resizeMeshes(meshes) {
		for (const mesh of meshes) {
			const imageIndex = mesh.assets && mesh.assets.length ? mesh.assets[0].image.index : mesh.index;
			const image = this.images[imageIndex];
			const { ref, cache } = image;
			const offsets = getRootOffset(cache);

			image.bounds = {
				imageWidth: ref.offsetWidth,
				imageHeight: ref.offsetHeight,
				width: cache.offsetWidth,
				height: cache.offsetHeight,
				top: offsets.top,
				left: offsets.left
			};

			mesh.resize({
				sizes: this.sizes,
				bounds: image.bounds
			});
		}
	}

	/**
	 * Set.
	 */
	set() {
		for (const hero of this.glHeroes) {
			if (hero && hero.update) {
				hero.update(this.scroll);
			}
		}
	}

	/**
	 * Update.
	 */
	update(scroll) {
		this.scroll = scroll;

		for (const gallery of this.glGalleries) {
			if (gallery && gallery.update) {
				gallery.update(this.scroll);
			}
		}
	}

}
